import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Palette, Upload, X } from 'lucide-react'
import { updateTenant, uploadTenantLogo, deleteTenantLogo } from '../../services/auth-api'
import { useToast } from '../../hooks/useToast'
import { parseApiError } from '../../utils/error-parser'
import { validateHexColor } from '../../utils/validation'
import { useOrganization } from '../../hooks/useOrganization'
import { usePermission } from '../../../hooks/usePermission'
import { getContrastText } from '../../../utils/color'
import { DEFAULT_BRANDING } from '../../../config/branding'
import { BrandingLivePreview } from '../../../pages/settings/BrandingLivePreview'

const LOGO_MAX_BYTES = 2 * 1024 * 1024
const LOGO_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png']
const LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png']
const LOGO_HELP_TEXT = 'PNG or JPG · Max 2 MB. Applies on Save Settings.'

function validateLogoFile(file: File): string | null {
  const dotIndex = file.name.lastIndexOf('.')
  const ext = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : ''
  if (!LOGO_ALLOWED_EXTENSIONS.includes(ext)) {
    return `File type "${ext || 'unknown'}" is not allowed. Accepted: ${LOGO_ALLOWED_EXTENSIONS.join(', ')}.`
  }
  if (file.type && !LOGO_ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return `Content type "${file.type}" is not allowed. Accepted: ${LOGO_ALLOWED_TYPES.join(', ')}.`
  }
  if (file.size === 0) {
    return 'The selected file is empty.'
  }
  if (file.size > LOGO_MAX_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1)
    return `File size (${sizeMb} MB) exceeds 2 MB limit.`
  }
  return null
}

type SettingsTab = 'identity' | 'branding'

interface BrandingForm {
  primary_color: string
  nav_background_color: string
  nav_text_color: string
  button_text_color: string
  /** True when the admin has explicitly picked a button text colour. */
  button_text_touched: boolean
}

const initialBranding = (
  primary: string | null,
  nav_bg: string | null,
  nav_text: string | null,
  btn_text: string | null,
): BrandingForm => ({
  primary_color: primary || DEFAULT_BRANDING.PRIMARY_COLOR,
  nav_background_color: nav_bg || DEFAULT_BRANDING.NAV_BACKGROUND_COLOR,
  nav_text_color: nav_text || DEFAULT_BRANDING.NAV_TEXT_COLOR,
  button_text_color: btn_text || getContrastText(primary || DEFAULT_BRANDING.PRIMARY_COLOR),
  button_text_touched: !!btn_text,
})

interface ColorFieldProps {
  label: string
  value: string
  onChange: (color: string) => void
  disabled?: boolean
  error?: string
  inputId: string
}

/** Native colour swatch + hex text input, both bound to the same setter. */
function ColorField({
  label,
  value,
  onChange,
  disabled,
  error,
  inputId,
}: ColorFieldProps) {
  // Local text buffer so partial edits ("#18004") don't clobber the parent
  // state until a complete 6-char hex is committed. Always stores the
  // display form: `#RRGGBB` uppercase, matching the mockup.
  const [text, setText] = useState(value.toUpperCase())

  useEffect(() => {
    setText(value.toUpperCase())
  }, [value])

  const commitText = (raw: string) => {
    const digits = raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toUpperCase()
    const next = `#${digits}`
    setText(next)
    if (digits.length === 6) {
      onChange(next)
    }
  }

  return (
    <div>
      <p className="text-gray-900 text-sm font-medium mb-2">{label}</p>
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="color"
          value={value}
          onChange={e => onChange(e.target.value.toUpperCase())}
          disabled={disabled}
          className={`color-swatch w-10 h-10 rounded-lg border border-gray-300 ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          aria-label={`${label} colour picker`}
        />
        <input
          type="text"
          value={text}
          onChange={e => commitText(e.target.value)}
          maxLength={7}
          disabled={disabled}
          className={`flex-1 min-w-0 px-3 py-2 font-mono text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${
            error ? 'border-red-300' : 'border-gray-300'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          aria-label={`${label} hex value`}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}

export function OnboardingSettings() {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const { data: orgData, isLoading } = useOrganization()
  const { can } = usePermission()
  const canEdit = can('manage_settings')

  const [activeTab, setActiveTab] = useState<SettingsTab>('identity')
  const [isSaving, setIsSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null)
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null)
  // True while the admin has clicked "Remove" but hasn't saved yet. Removal
  // is staged (like uploads) — the DELETE only fires on Save so a page
  // refresh restores the previous logo. Bug fix 2026-07-16.
  const [pendingLogoRemove, setPendingLogoRemove] = useState(false)

  const [form, setForm] = useState<BrandingForm>(() =>
    initialBranding(null, null, null, null),
  )
  const [companyName, setCompanyName] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  // Mirror server state into the form only while clean, so background
  // refetches never overwrite unsaved user edits.
  useEffect(() => {
    if (orgData && !isDirty) {
      setForm(
        initialBranding(
          orgData.primary_color,
          orgData.nav_background_color,
          orgData.nav_text_color,
          orgData.button_text_color,
        ),
      )
      setCompanyName(orgData.name || '')
    }
  }, [orgData, isDirty])

  // Revoke pending logo preview URL on cleanup or replacement.
  useEffect(() => {
    if (!pendingLogoPreview) return
    return () => URL.revokeObjectURL(pendingLogoPreview)
  }, [pendingLogoPreview])

  const patchForm = (patch: Partial<BrandingForm>) => {
    setForm(prev => ({ ...prev, ...patch }))
    // Deliberately no `applyBrandColors` call here. The old code live-
    // applied CSS variables on every form edit, which leaked unsaved colour
    // picks into the sidebar and other page chrome. Client feedback:
    // "display live changes only in the preview section." The preview reads
    // colours from React props (not CSS variables), so it still updates on
    // every keystroke. CSS variables only refresh after Save triggers a
    // tenant-data refetch → `ProtectedRoute` effect → `applyBrandColors`.
    setIsDirty(true)
    setErrors(prev => {
      const n = { ...prev }
      Object.keys(patch).forEach(k => delete n[k])
      return n
    })
  }

  const clearPendingLogo = () => {
    setPendingLogoFile(null)
    setPendingLogoPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const clearPendingLogoRemove = () => {
    setPendingLogoRemove(false)
  }

  const handleReset = () => {
    // Reset only the four Branding colours per Q10 — company name/logo
    // remain untouched. No `applyBrandColors` — same rationale as
    // patchForm: live changes are confined to the preview; sidebar and
    // page chrome stay at the saved theme until the user clicks Save.
    setForm(prev => ({
      ...prev,
      primary_color: DEFAULT_BRANDING.PRIMARY_COLOR,
      nav_background_color: DEFAULT_BRANDING.NAV_BACKGROUND_COLOR,
      nav_text_color: DEFAULT_BRANDING.NAV_TEXT_COLOR,
      button_text_color: getContrastText(DEFAULT_BRANDING.PRIMARY_COLOR),
      button_text_touched: false,
    }))
    setErrors({})
    setFormError(null)
    setIsDirty(true)
  }

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const clientError = validateLogoFile(file)
    if (clientError) {
      addToast(clientError, 'error')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setPendingLogoFile(file)
    setPendingLogoPreview(URL.createObjectURL(file))
    // Picking a new file overrides any staged removal.
    setPendingLogoRemove(false)
    setIsDirty(true)
  }

  const handleLogoRemove = () => {
    // Stage the removal; the DELETE fires from `handleSave` alongside colour
    // updates. Refreshing the page before Save restores the original logo.
    clearPendingLogo()
    setPendingLogoRemove(true)
    setIsDirty(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const errs: Record<string, string> = {}
    const primaryErr = validateHexColor(form.primary_color)
    if (primaryErr) errs.primary_color = primaryErr
    const navBgErr = validateHexColor(form.nav_background_color)
    if (navBgErr) errs.nav_background_color = navBgErr
    const navTextErr = validateHexColor(form.nav_text_color)
    if (navTextErr) errs.nav_text_color = navTextErr
    if (form.button_text_touched) {
      const btnTextErr = validateHexColor(form.button_text_color)
      if (btnTextErr) errs.button_text_color = btnTextErr
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setIsSaving(true)
    const invalidateTenantCaches = () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-tenant'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-me'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-settings'] }),
      ])

    let logoPersisted = false
    try {
      // Upload wins over removal — picking a new file after clicking Remove
      // just replaces the logo. Only fire DELETE when the admin explicitly
      // staged a removal AND didn't pick a replacement.
      if (pendingLogoFile) {
        await uploadTenantLogo(pendingLogoFile)
        logoPersisted = true
        clearPendingLogo()
        clearPendingLogoRemove()
      } else if (pendingLogoRemove) {
        await deleteTenantLogo()
        logoPersisted = true
        clearPendingLogoRemove()
      }

      // Company name is read-only on this screen (2026-07-15), so we
      // deliberately omit it from the PATCH. The backend treats a `name`
      // key as a rename and syncs it to WorkOS
      // (`backend/tenants/views.py:42-50`) — sending the local, possibly
      // stale, value would let this tab overwrite a SaaS-admin rename made
      // while the page was open.
      await updateTenant({
        primary_color: form.primary_color,
        nav_background_color: form.nav_background_color,
        nav_text_color: form.nav_text_color,
        // Null explicitly when the admin hasn't touched the field — this
        // preserves the auto-derived contrast fallback on the frontend.
        button_text_color: form.button_text_touched ? form.button_text_color : null,
      })

      await invalidateTenantCaches()
      addToast('Settings saved successfully!', 'success')
      setErrors({})
      setIsDirty(false)
    } catch (err) {
      if (logoPersisted) {
        await invalidateTenantCaches().catch(() => {})
      }
      const parsed = parseApiError(err)
      setFormError(parsed.message)
      addToast(parsed.message, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary" />
      </div>
    )
  }

  return (
    <div>
      <div>
        {/* Match the deployed mockup at /admin/settings verbatim:
         * `h1.text-2xl sm:text-3xl font-bold text-gray-900` (30px) +
         * `p.text-gray-500 mt-1` (16px, default text-base). Earlier bump
         * to text-3xl/4xl overshot the mockup by one step. */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-500">
          Configure company identity and brand appearance
        </p>
      </div>

      {!canEdit && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-700">You have view-only access to settings.</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <div className="flex gap-0">
          <button
            type="button"
            onClick={() => setActiveTab('identity')}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'identity'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Company identity
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('branding')}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'branding'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Branding
          </button>
        </div>
      </div>

      {formError && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{formError}</p>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Company identity tab */}
        {activeTab === 'identity' && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-gray-900 text-xl font-bold mb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Company identity
              </h2>
              <p className="text-gray-500 text-sm">
                Set your company name and logo — shown in the engineer app header
                and admin sidebar.
              </p>
            </div>

            <div className="space-y-6">
              {/* Company Name — read-only display of the tenant name.
                * Renaming a company is intentionally not supported from this
                * screen (2026-07-15 client request): the name is provisioned
                * during tenant onboarding and any change needs to flow
                * through the SaaS admin instead so WorkOS org, billing and
                * audit records stay in sync. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company name
                </label>
                <input
                  type="text"
                  value={companyName}
                  readOnly
                  disabled
                  aria-readonly="true"
                  tabIndex={-1}
                  placeholder="—"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed select-text"
                />
              </div>

              {/* Logo Upload — mockup-style: single dashed-border zone that
               * either previews the current/pending logo or prompts for one. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <label
                  className={`block ${canEdit && !isUploading && !isSaving ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-brand-primary/30 hover:bg-gray-50/50 transition-colors">
                    {/* Removal is staged. The current logo stays untouched on the
                     * backend until Save runs — show the "will be removed" state
                     * with an Undo affordance so the admin can back out. */}
                    {pendingLogoRemove && !pendingLogoPreview ? (
                      <div className="flex flex-col items-center text-center py-2">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <Upload className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">
                          Logo will be removed on save
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Click to upload a new logo, or undo the removal.
                        </p>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault()
                              clearPendingLogoRemove()
                            }}
                            className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-brand-primary hover:bg-brand-primary-lightest transition-colors"
                          >
                            Undo remove
                          </button>
                        )}
                      </div>
                    ) : pendingLogoPreview || orgData?.logo_url ? (
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                          <img
                            src={pendingLogoPreview || orgData?.logo_url || ''}
                            alt="Logo"
                            className="w-full h-full object-contain p-1.5"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {pendingLogoPreview ? 'New logo selected' : 'Logo uploaded'}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {pendingLogoPreview
                              ? 'Applies on Save Settings'
                              : 'Click to replace or drag a new file'}
                          </p>
                          {canEdit && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors">
                                <Upload className="w-3 h-3" />
                                Change
                              </span>
                              <button
                                type="button"
                                onClick={e => {
                                  e.preventDefault()
                                  if (pendingLogoPreview) {
                                    clearPendingLogo()
                                  } else {
                                    handleLogoRemove()
                                  }
                                }}
                                disabled={isUploading || isSaving}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                              >
                                <X className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-center py-2">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <Upload className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">
                          Click to upload logo
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {LOGO_HELP_TEXT}
                        </p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleLogoSelect}
                    disabled={!canEdit || isUploading || isSaving}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Branding tab */}
        {activeTab === 'branding' && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-gray-900 text-xl font-bold mb-2 flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Brand appearance
              </h2>
              <p className="text-gray-500 text-sm">
                Customise colours and see a live preview of the engineer app.
              </p>
            </div>

            {/* Live preview */}
            <div className="mb-8">
              <p className="text-gray-700 text-sm font-medium mb-3">Live preview</p>
              <BrandingLivePreview
                navBackgroundColor={form.nav_background_color}
                navTextColor={form.nav_text_color}
                primaryColor={form.primary_color}
                buttonTextColor={
                  form.button_text_touched
                    ? form.button_text_color
                    : getContrastText(form.primary_color)
                }
              />
            </div>

            {/* Navigation colours */}
            <div className="mb-8">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Navigation
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ColorField
                  inputId="nav-bg"
                  label="Background"
                  value={form.nav_background_color}
                  onChange={c => patchForm({ nav_background_color: c })}
                  disabled={!canEdit}
                  error={errors.nav_background_color}
                />
                <ColorField
                  inputId="nav-text"
                  label="Text"
                  value={form.nav_text_color}
                  onChange={c => patchForm({ nav_text_color: c })}
                  disabled={!canEdit}
                  error={errors.nav_text_color}
                />
              </div>
            </div>

            <div className="border-t border-gray-100 mb-8" />

            {/* Button colours */}
            <div className="mb-8">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Buttons
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ColorField
                  inputId="btn-bg"
                  label="Background"
                  value={form.primary_color}
                  onChange={c => patchForm({ primary_color: c })}
                  disabled={!canEdit}
                  error={errors.primary_color}
                />
                <ColorField
                  inputId="btn-text"
                  label="Text"
                  value={
                    form.button_text_touched
                      ? form.button_text_color
                      : getContrastText(form.primary_color)
                  }
                  onChange={c =>
                    patchForm({ button_text_color: c, button_text_touched: true })
                  }
                  disabled={!canEdit}
                  error={errors.button_text_color}
                />
              </div>
            </div>

            {/* Reset to Defaults */}
            {canEdit && (
              <div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border-2 border-brand-primary text-brand-primary rounded-lg font-medium text-sm hover:bg-brand-primary hover:text-[var(--brand-primary-text)] transition-colors"
                >
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        )}

        {/* Shared Save button — commits both tabs together (per §5.1 v4). */}
        {canEdit && (
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2.5 bg-brand-primary text-[var(--brand-primary-text)] rounded-lg hover:bg-brand-primary-hover transition-colors font-medium text-sm disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
